/*
 * Đồng bộ lời mời khám, phiếu khám, chữ ký với /api/v2/reception/visits/{id}/exam (A22).
 * Các màn vẫn ghi vào store như cũ. File này theo dõi store để gửi thay đổi lên server, và tải phiếu về khi người khác đổi.
 * Lỗi 404 (môi trường chưa có API) thì giữ cách cũ: chỉ lưu trên máy.
 */
import { useEffect } from 'react';
import { message } from 'antd';
import { apiRequest } from '../Telesales/lead/api';
import { allCodes, doctorName, type Visit } from './logic';
import { loadApiServices } from './catalog';
import { applyServerExam, examPutBody, examStamp, examStateActive, examStateOf, type ExamState, type ServerExam } from './examSync';
import { loadCardPackages } from './packages';
import { draftQuote } from './quoteDraft';
import { useTuVan } from './store';

const BASE = '/api/v2/reception/visits';
const SAVE_DELAY = 500;

const ERR: Record<string, string> = {
  EXAM_SIGNED: 'Phiếu khám đã ký. Huỷ ký trước khi sửa.',
  EXAM_NOT_SIGNED: 'Phiếu khám chưa ký.',
  EXAM_OTHER_DOCTOR: 'Phiếu này của bác sĩ khác, tài khoản của bạn không sửa được.',
  VISIT_STATE_INVALID: 'Lượt đã xong hoặc đã huỷ, không sửa được phiếu khám.',
  STAFF_NOT_READY: 'Bác sĩ đang tạm nghỉ hoặc ngoại tuyến. Chọn bác sĩ khác.',
  STAFF_INVALID: 'Bác sĩ không đúng vai trò hoặc thuộc cơ sở khác.',
};

/** Bản đã khớp với server của từng lượt */
const synced = new Map<number, ExamState>();
/** Mốc của lần tải phiếu gần nhất */
const pulled = new Map<number, string>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();
const running = new Set<number>();
const pulling = new Set<number>();
/** Lượt tài khoản này không được đọc phiếu (403: cơ sở khác, bác sĩ khác): chỉ bỏ lượt đó, không tắt cả phiên */
const denied = new Set<number>();
let applying = false;
/** Môi trường chưa có API phiếu khám (404) */
let off = false;
/** Mất mạng, lỗi máy chủ: chờ rồi gửi lại, không dồn dập */
const RETRY_MS = 30_000;

const findVisit = (id: number) => useTuVan.getState().visits.find((v) => v.serverId === id);
const active = (v: Visit) => v.status !== 'done' && v.status !== 'cancelled';
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Dòng server chưa có trên máy mà danh mục chưa tải được: nhận vào sẽ thành dòng giá 0 */
const missingPrices = (v: Visit, e: ServerExam, services: { id: number }[], packages: { card: { apiId?: number } }[]) =>
  (e.lines ?? []).some((s) => (s.service_id
    ? !v.quote?.lines.some((l) => l.apiId === s.service_id) && !services.some((x) => x.id === s.service_id)
    : s.package_id ? !v.quote?.lines.some((l) => l.card?.apiId === s.package_id) && !packages.some((x) => x.card.apiId === s.package_id) : false));

const applyExam = async (id: number, e: ServerExam) => {
  const [services, packages] = await Promise.all([loadApiServices(), loadCardPackages()]);
  const v = findVisit(id);
  if (!v) return;
  // Chưa ghi mốc đã tải: lần mở lượt sau tải lại, lúc đó danh mục có thể đã tải được
  if (missingPrices(v, e, services, packages)) {
    message.warning({ key: 'exam-catalog', content: 'Chưa tải được danh mục dịch vụ, dịch vụ bác sĩ chỉ định chưa vào được phiếu báo giá. Tải lại trang để thử lại.' });
    return;
  }
  const patch = applyServerExam(v, e, { services, packages }, (x) => draftQuote(x, doctorName(x.consultant), allCodes(useTuVan.getState().visits)));
  applying = true;
  useTuVan.getState().update(v.id, () => patch);
  applying = false;
  const after = findVisit(id);
  if (after) synced.set(id, examStateOf(after));
  pulled.set(id, examStamp(e) ?? '');
};

/** Tải phiếu đầy đủ về. Đang có thay đổi chưa gửi thì để lần sau, tránh đè phần vừa sửa */
export async function pullExam(id: number) {
  if (off || denied.has(id) || timers.has(id) || running.has(id) || pulling.has(id)) return;
  pulling.add(id);
  try {
    const r = await apiRequest<ServerExam | null>(`${BASE}/${id}/exam`);
    if (r.status === 404) { off = true; return; }
    if (r.status === 403) { denied.add(id); return; }
    if (!r.ok || !r.data || timers.has(id) || running.has(id)) return;
    await applyExam(id, r.data);
  } finally {
    pulling.delete(id);
  }
}

/** Tư vấn viên mời bác sĩ: chờ server trả lời để báo đúng khi bác sĩ đang nghỉ. Môi trường chưa có API thì coi như được */
export async function inviteDoctor(v: Visit, doctor: string): Promise<{ ok: boolean; msg?: string }> {
  if (off || !v.serverId || !/^\d+$/.test(doctor)) return { ok: true };
  const r = await apiRequest<ServerExam>(`${BASE}/${v.serverId}/exam/invite`, { method: 'POST', body: JSON.stringify({ doctor_id: Number(doctor) }) });
  if (r.status === 404) { off = true; return { ok: true }; }
  if (!r.ok || !r.data) return { ok: false, msg: (r.code && ERR[r.code]) || r.message };
  await applyExam(v.serverId, r.data);
  return { ok: true };
}

const call = (id: number, path: string, method: 'POST' | 'PUT', body: unknown) =>
  apiRequest<ServerExam>(`${BASE}/${id}/exam${path}`, { method, body: JSON.stringify(body) });

/**
 * Gửi phần khác nhau giữa bản đã khớp và bản hiện tại.
 * done: đã khớp (hoặc không cần gửi nữa) · retry: mất mạng, lỗi máy chủ, gửi lại sau · rejected: server từ chối, đã hiện lại bản server
 */
async function push(id: number): Promise<'done' | 'retry' | 'rejected'> {
  const v = findVisit(id);
  const prev = synced.get(id);
  if (!v || !prev || off || denied.has(id)) return 'done';
  const next = examStateOf(v);
  const steps: (() => ReturnType<typeof call>)[] = [];
  if (prev.signed && !next.signed) steps.push(() => call(id, '/unsign', 'POST', {}));
  if (next.invite !== null && next.invite !== prev.invite && !prev.signed) steps.push(() => call(id, '/invite', 'POST', { doctor_id: next.invite }));
  const body = examPutBody(prev, next);
  if (Object.keys(body).length) steps.push(() => call(id, '', 'PUT', body));
  const signature = v.quote?.exam.sign;
  const signNow = next.signed && !prev.signed;
  if (signNow && signature) steps.push(() => call(id, '/sign', 'POST', { signature }));

  let last: ServerExam | null = null;
  for (let i = 0; i < steps.length; i += 1) {
    // Các bước phải chạy lần lượt: huỷ ký, sửa, rồi ký lại
    // eslint-disable-next-line no-await-in-loop
    const r = await steps[i]();
    if (r.status === 404) { off = true; return 'done'; }
    if (!r.ok || !r.data) {
      message.error({ key: `exam-${id}`, content: `Chưa lưu được phiếu khám: ${(r.code && ERR[r.code]) || r.message}` });
      // Mất mạng, lỗi máy chủ: giữ để gửi lại sau
      if (!r.status || r.status >= 500) return 'retry';
      // Server từ chối thì hiện lại bản trên server
      // eslint-disable-next-line no-await-in-loop
      const fresh = await apiRequest<ServerExam | null>(`${BASE}/${id}/exam`);
      if (fresh.ok && fresh.data) await applyExam(id, fresh.data);
      else if (fresh.ok) synced.set(id, examStateOf(v));
      return 'rejected';
    }
    last = r.data;
  }
  // Ký mà chưa có ảnh chữ ký: server chưa ký nên không coi là đã khớp phần ký; lưu chữ ký xong sẽ gửi lại
  if (signNow && !signature) {
    message.warning({ key: `exam-${id}`, content: 'Phiếu khám chưa có chữ ký nên chưa ký trên server. Ký lại để lưu.' });
    synced.set(id, { ...next, signed: false });
  } else synced.set(id, next);
  if (last) pulled.set(id, examStamp(last) ?? '');
  return 'done';
}

const schedule = (id: number, delay = SAVE_DELAY) => {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.set(id, setTimeout(async () => {
    timers.delete(id);
    if (running.has(id)) { schedule(id); return; }
    running.add(id);
    const before = findVisit(id);
    const sent = before ? examStateOf(before) : undefined;
    let res: 'done' | 'retry' | 'rejected' = 'done';
    try { res = await push(id); } finally { running.delete(id); }
    if (off) return;
    if (res === 'retry') { schedule(id, RETRY_MS); return; }
    // Có thay đổi trong lúc đang gửi: chỉ khi bản hiện tại khác bản vừa gửi, không gửi lặp lại cùng một bản
    const v = findVisit(id);
    if (res === 'done' && v && active(v) && !same(examStateOf(v), sent) && !same(examStateOf(v), synced.get(id))) schedule(id);
  }, delay));
};

const checkVisit = (v: Visit) => {
  const id = v.serverId;
  if (!id) return;
  if (!synced.has(id)) synced.set(id, examStateOf(v));
  const s = synced.get(id)!;
  const next = examStateOf(v);
  if (active(v) && examStateActive(next) && !same(s, next)) schedule(id);
  // Chỉ tải lại phiếu của lượt đang mở; lượt khác tải khi mở (useExamOpen), không dồn hàng chục request lúc vào app
  if (active(v) && v.id === useTuVan.getState().cur && v.examStamp && v.examStamp !== pulled.get(id)) pullExam(id);
};

let started = false;
const start = () => {
  if (started) return;
  started = true;
  useTuVan.getState().visits.forEach(checkVisit);
  useTuVan.subscribe((s, p) => {
    if (applying || off || s.visits === p.visits) return;
    const before = new Map(p.visits.map((v) => [v.id, v]));
    s.visits.forEach((v) => { if (before.get(v.id) !== v) checkVisit(v); });
  });
};

/** Chạy ở App Tư vấn. Màn Lễ tân chỉ cần lời mời, đã có trong danh sách lượt */
export function useExamSync() {
  useEffect(() => { start(); }, []);
}

/** Mở một lượt: tải phiếu mới nhất, gồm cả lượt đã xong */
export function useExamOpen(v?: Visit) {
  const id = v?.serverId;
  useEffect(() => { if (id) pullExam(id); }, [id]);
}
